import "./AssetsPage.css";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type CustomerOption = {
  id: string;
  name: string;
  status: "active" | "inactive" | "archived";
};

type AssetRow = {
  id: string;
  customer_id: string;
  customer_name: string;
  asset_type: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  identifier: string | null;
  color: string | null;
  year: number | null;
  description: string | null;
  notes: string | null;
  status: "active" | "inactive" | "archived";
  updated_at: string;
};

type AssetForm = {
  customerId: string;
  assetType: string;
  brand: string;
  model: string;
  serialNumber: string;
  identifier: string;
  color: string;
  year: string;
  description: string;
  notes: string;
};

const EMPTY_FORM: AssetForm = {
  customerId: "",
  assetType: "",
  brand: "",
  model: "",
  serialNumber: "",
  identifier: "",
  color: "",
  year: "",
  description: "",
  notes: "",
};

function nullable(value: string) {
  const clean = value.trim();
  return clean ? clean : null;
}

export function AssetsPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [customerFilter, setCustomerFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AssetForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!organizationId) {
      setCustomers([]);
      setAssets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const [customersResult, assetsResult] = await Promise.all([
      supabase.rpc("list_organization_customers", {
        p_organization_id: organizationId,
        p_search: null,
      }),
      supabase.rpc("list_customer_assets", {
        p_organization_id: organizationId,
        p_customer_id: customerFilter || null,
      }),
    ]);

    if (customersResult.error || assetsResult.error) {
      setLoadError(
        customersResult.error?.message ??
          assetsResult.error?.message ??
          "No se pudieron cargar los activos.",
      );
    }

    setCustomers(
      customersResult.error
        ? []
        : ((customersResult.data ?? []) as CustomerOption[]).filter(
            (customer) => customer.status === "active",
          ),
    );
    setAssets(
      assetsResult.error ? [] : ((assetsResult.data ?? []) as AssetRow[]),
    );
    setLoading(false);
  }, [organizationId, customerFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setCustomerFilter("");
    setForm(EMPTY_FORM);
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
    setFormSuccess(null);
  }, [organizationId]);

  const activeCount = useMemo(
    () => assets.filter((asset) => asset.status === "active").length,
    [assets],
  );

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(false);
  }

  function startEdit(asset: AssetRow) {
    setEditingId(asset.id);
    setForm({
      customerId: asset.customer_id,
      assetType: asset.asset_type,
      brand: asset.brand ?? "",
      model: asset.model ?? "",
      serialNumber: asset.serial_number ?? "",
      identifier: asset.identifier ?? "",
      color: asset.color ?? "",
      year: asset.year == null ? "" : String(asset.year),
      description: asset.description ?? "",
      notes: asset.notes ?? "",
    });
    setFormError(null);
    setFormSuccess(null);
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;

    if (!form.customerId) {
      setFormError("Selecciona un cliente.");
      return;
    }

    if (!form.assetType.trim()) {
      setFormError("Ingresa el tipo de activo.");
      return;
    }

    const year = form.year.trim() ? Number(form.year) : null;

    if (
      year !== null &&
      (!Number.isInteger(year) || year < 1900 || year > 2100)
    ) {
      setFormError("El año debe estar entre 1900 y 2100.");
      return;
    }

    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const payload = {
      p_customer_id: form.customerId,
      p_asset_type: form.assetType,
      p_brand: nullable(form.brand),
      p_model: nullable(form.model),
      p_serial_number: nullable(form.serialNumber),
      p_identifier: nullable(form.identifier),
      p_color: nullable(form.color),
      p_year: year,
      p_description: nullable(form.description),
      p_notes: nullable(form.notes),
    };

    const { error } = editingId
      ? await supabase.rpc("update_customer_asset", {
          p_asset_id: editingId,
          ...payload,
        })
      : await supabase.rpc("create_customer_asset", {
          p_organization_id: organizationId,
          ...payload,
        });

    setSaving(false);

    if (error) {
      if (error.message.includes("ASSET_CUSTOMER_LOCKED_BY_SERVICE_HISTORY")) {
        setFormError(
          "No se puede cambiar el cliente porque el activo ya tiene historial de taller.",
        );
      } else {
        setFormError(error.message);
      }
      return;
    }

    const wasEditing = Boolean(editingId);
    const keepCustomerId = form.customerId;
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      customerId: wasEditing ? "" : keepCustomerId,
    });
    if (wasEditing) setFormOpen(false);
    setFormSuccess(
      wasEditing
        ? "Activo actualizado correctamente."
        : "Activo registrado correctamente.",
    );
    await loadData();
  }

  async function changeStatus(asset: AssetRow) {
    const nextStatus = asset.status === "active" ? "inactive" : "active";

    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const { error } = await supabase.rpc("set_customer_asset_status", {
      p_asset_id: asset.id,
      p_status: nextStatus,
    });

    setSaving(false);

    if (error) {
      if (error.message.includes("ASSET_HAS_OPEN_SERVICE_ORDER")) {
        setFormError(
          "No se puede desactivar el activo porque tiene una orden de taller abierta.",
        );
      } else {
        setFormError(error.message);
      }
      return;
    }

    setFormSuccess(
      nextStatus === "active" ? "Activo activado." : "Activo desactivado.",
    );
    await loadData();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con activos.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Activos</span>
          <h1>{selectedOrganization.name}</h1>
          <p>
            Equipos, vehículos, máquinas u otros bienes asociados a cada cliente.
          </p>
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={() => {
            if (formOpen) {
              resetForm();
            } else {
              setEditingId(null);
              setForm(EMPTY_FORM);
              setFormOpen(true);
            }
            setFormError(null);
            setFormSuccess(null);
          }}
        >
          {formOpen ? "Cerrar" : "Nuevo activo"}
        </button>
      </header>

      <section className="asset-summary-grid">
        <div className="metric-card">
          <span>Activos visibles</span>
          <strong>{loading ? "…" : assets.length}</strong>
        </div>
        <div className="metric-card">
          <span>Activos vigentes</span>
          <strong>{loading ? "…" : activeCount}</strong>
        </div>
        <div className="metric-card">
          <span>Clientes disponibles</span>
          <strong>{loading ? "…" : customers.length}</strong>
        </div>
      </section>

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                {editingId ? "Edición" : "Registro"}
              </span>
              <h2>{editingId ? "Editar activo" : "Nuevo activo"}</h2>
            </div>
          </div>

          <form className="asset-form" onSubmit={handleCreate}>
            <label className="field-span-2">
              Cliente
              <select
                required
                value={form.customerId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    customerId: event.target.value,
                  }))
                }
              >
                <option value="">Selecciona un cliente</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tipo de activo
              <input
                required
                placeholder="Ej. Laptop, compresor, vehículo"
                value={form.assetType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    assetType: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Marca
              <input
                value={form.brand}
                onChange={(event) =>
                  setForm((current) => ({ ...current, brand: event.target.value }))
                }
              />
            </label>

            <label>
              Modelo
              <input
                value={form.model}
                onChange={(event) =>
                  setForm((current) => ({ ...current, model: event.target.value }))
                }
              />
            </label>

            <label>
              Serie
              <input
                value={form.serialNumber}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    serialNumber: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Identificador
              <input
                placeholder="Placa, código interno, etiqueta"
                value={form.identifier}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    identifier: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Color
              <input
                value={form.color}
                onChange={(event) =>
                  setForm((current) => ({ ...current, color: event.target.value }))
                }
              />
            </label>

            <label>
              Año
              <input
                type="number"
                min="1900"
                max="2100"
                value={form.year}
                onChange={(event) =>
                  setForm((current) => ({ ...current, year: event.target.value }))
                }
              />
            </label>

            <label className="field-span-2">
              Descripción
              <input
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>

            <label className="field-span-full">
              Notas
              <input
                value={form.notes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, notes: event.target.value }))
                }
              />
            </label>

            {formError ? (
              <div className="form-error field-span-full">{formError}</div>
            ) : null}

            {formSuccess ? (
              <div className="form-success field-span-full">{formSuccess}</div>
            ) : null}

            <div className="form-actions field-span-full">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving
                  ? "Guardando…"
                  : editingId
                    ? "Guardar cambios"
                    : "Guardar activo"}
              </button>
              {editingId ? (
                <button
                  className="ghost-button"
                  type="button"
                  disabled={saving}
                  onClick={resetForm}
                >
                  Cancelar edición
                </button>
              ) : null}
            </div>
          </form>
        </section>
      ) : null}

      <section className="toolbar-card asset-toolbar">
        <div>
          <strong>{loading ? "…" : assets.length}</strong>
          <span> activos</span>
        </div>

        <select
          value={customerFilter}
          onChange={(event) => setCustomerFilter(event.target.value)}
        >
          <option value="">Todos los clientes</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && assets.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay activos para mostrar.</strong>
          <p>Registra el primer activo de un cliente.</p>
        </section>
      ) : null}

      <section className="asset-grid" aria-label="Activos de clientes">
        {assets.map((asset) => (
          <article className="asset-card" key={asset.id}>
            <div className="asset-card-heading">
              <div>
                <span className="status-badge">{asset.asset_type}</span>
                <h2>
                  {[asset.brand, asset.model].filter(Boolean).join(" ") ||
                    asset.identifier ||
                    "Activo"}
                </h2>
                <p>{asset.customer_name}</p>
              </div>

              <span
                className={
                  asset.status === "active"
                    ? "status-dot status-dot-active"
                    : "status-dot"
                }
                title={asset.status}
              />
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Serie</dt>
                <dd>{asset.serial_number ?? "—"}</dd>
              </div>
              <div>
                <dt>Identificador</dt>
                <dd>{asset.identifier ?? "—"}</dd>
              </div>
              <div>
                <dt>Color</dt>
                <dd>{asset.color ?? "—"}</dd>
              </div>
              <div>
                <dt>Año</dt>
                <dd>{asset.year ?? "—"}</dd>
              </div>
            </dl>

            {asset.description ? (
              <p className="asset-description">{asset.description}</p>
            ) : null}

            <div className="quotation-actions">
              <button
                className="compact-button"
                type="button"
                disabled={saving}
                onClick={() => startEdit(asset)}
              >
                Editar
              </button>
              <button
                className="compact-button"
                type="button"
                disabled={saving}
                onClick={() => void changeStatus(asset)}
              >
                {asset.status === "active" ? "Desactivar" : "Activar"}
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
